import { en } from './translations/en';
import { STORAGE_KEYS } from '../constants/constants';

class I18nService {
  constructor() {
    this.translations = {
      en,
    };
    this.currentLanguage = this.getStoredLanguage() || 'en';
    this.fallbackLanguage = 'en';
  }

  // Get stored language from localStorage
  getStoredLanguage() {
    return localStorage.getItem(STORAGE_KEYS.LANGUAGE);
  }

  // Set and store language
  setLanguage(language) {
    if (this.translations[language]) {
      this.currentLanguage = language;
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
      // Trigger language change event
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: language }));
    } else {
      // console.warn(`Language '${language}' not supported. Available languages:`, Object.keys(this.translations));
    }
  }

  // Get current language
  getCurrentLanguage() {
    return this.currentLanguage;
  }

  // Get available languages
  getAvailableLanguages() {
    return Object.keys(this.translations);
  }

  // Get translation by key
  translate(key, params = {}) {
    const keys = key.split('.');
    let translation = this.translations[this.currentLanguage];

    // Navigate through nested keys
    for (const k of keys) {
      if (translation && typeof translation === 'object' && k in translation) {
        translation = translation[k];
      } else {
        // Fallback to default language
        translation = this.translations[this.fallbackLanguage];
        for (const fallbackKey of keys) {
          if (translation && typeof translation === 'object' && fallbackKey in translation) {
            translation = translation[fallbackKey];
          } else {
            translation = key; // Return key if translation not found
            break;
          }
        }
        break;
      }
    }

    // If translation is still an object, return the key
    if (typeof translation === 'object') {
      return key;
    }

    // Replace parameters in translation
    if (typeof translation === 'string' && Object.keys(params).length > 0) {
      return this.interpolate(translation, params);
    }

    return translation || key;
  }

  // Interpolate parameters in translation string
  interpolate(str, params) {
    return str.replace(/\{(\w+)\}/g, (match, key) => {
      return params[key] !== undefined ? params[key] : match;
    });
  }

  // Format number based on current language
  formatNumber(number, options = {}) {
    const locale = this.getLocale();
    return new Intl.NumberFormat(locale, options).format(number);
  }

  // Format date based on current language
  formatDate(date, options = {}) {
    const locale = this.getLocale();
    return new Intl.DateTimeFormat(locale, options).format(new Date(date));
  }

  // Format currency based on current language
  formatCurrency(amount, currency = 'USD', options = {}) {
    const locale = this.getLocale();
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...options,
    }).format(amount);
  }

  // Format relative time (e.g., "2 hours ago")
  formatRelativeTime(date, options = {}) {
    const locale = this.getLocale();
    const rtf = new Intl.RelativeTimeFormat(locale, options);
    const now = new Date();
    const targetDate = new Date(date);
    const diffInSeconds = (targetDate - now) / 1000;

    const units = [
      { unit: 'year', seconds: 31536000 },
      { unit: 'month', seconds: 2592000 },
      { unit: 'week', seconds: 604800 },
      { unit: 'day', seconds: 86400 },
      { unit: 'hour', seconds: 3600 },
      { unit: 'minute', seconds: 60 },
      { unit: 'second', seconds: 1 },
    ];

    for (const { unit, seconds } of units) {
      const diff = Math.round(diffInSeconds / seconds);
      if (Math.abs(diff) >= 1) {
        return rtf.format(diff, unit);
      }
    }

    return rtf.format(0, 'second');
  }

  // Get locale for formatting
  getLocale() {
    const localeMap = {
      en: 'en-US',
      // Add more language to locale mappings as needed
    };
    return localeMap[this.currentLanguage] || 'en-US';
  }

  // Pluralization helper
  plural(count, singular, plural = null) {
    if (count === 1) {
      return singular;
    }
    return plural || `${singular}s`;
  }

  // Check if language is RTL (Right-to-Left)
  isRTL() {
    const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
    return rtlLanguages.includes(this.currentLanguage);
  }

  // Get text direction
  getTextDirection() {
    return this.isRTL() ? 'rtl' : 'ltr';
  }

  // Load language dynamically (for future implementation)
  async loadLanguage(language) {
    if (this.translations[language]) {
      return Promise.resolve();
    }

    try {
      // Dynamic import for code splitting
      const module = await import(`./translations/${language}.js`);
      this.translations[language] = module.default || module[language];
      return Promise.resolve();
    } catch (error) {
      // console.error(`Failed to load language '${language}':`, error);
      return Promise.reject(error);
    }
  }
}

// Create singleton instance
const i18nService = new I18nService();

// Export the service and a convenience function
export const t = (key, params) => i18nService.translate(key, params);
export const formatNumber = (number, options) => i18nService.formatNumber(number, options);
export const formatDate = (date, options) => i18nService.formatDate(date, options);
export const formatCurrency = (amount, currency, options) => i18nService.formatCurrency(amount, currency, options);
export const formatRelativeTime = (date, options) => i18nService.formatRelativeTime(date, options);
export const plural = (count, singular, plural) => i18nService.plural(count, singular, plural);

export default i18nService;